/**
 * Module dependencies.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Button } from 'src/components/core/buttons/button';
import { ButtonWrapper, Wrapper } from './steps';
import { CheckboxField } from 'src/components/core/forms/checkbox/field';
import { Control, UseFormRegister, UseFormWatch, useForm } from 'react-hook-form';
import { ErrorMessage, FormGroup, Label } from 'src/components/core/forms/styles';
import { FormData, useSchedule } from 'src/providers/schedule';
import { InputDateField } from 'src/components/core/forms/input-date/field';
import { InputField } from 'src/components/core/forms/input/field';
import { InputRadioField } from 'src/components/core/forms/input-radio/field';
import { RawHtml } from '@untile/react-core/components/raw-html';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { breakpoints } from '@untile/react-core/styles/breakpoints';
import { regexes } from 'src/core/constants/regexes';
import { transparentize } from 'src/styles/utils/colors';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useTranslation } from 'next-i18next';
import arrowRightSvg from 'src/assets/svgs/arrow-right.svg';
import infoSvg from 'src/assets/svgs/info.svg';
import styled from 'styled-components';

/**
 * `Form` styled component.
 */

const Form = styled.form`
  display: grid;
  grid-row-gap: var(--space-xxs);
  grid-template-columns: 100%;
`;

/**
 * `Card` styled component.
 */

const Card = styled(motion.div)`
  background-color: ${transparentize('cyan800', 0.16)};
  border-radius: var(--border-radius-xs);
  padding: var(--space-xs);
`;

/**
 * `Info` styled component.
 */

const Info = styled.div`
  color: var(--color-slate100);
  display: grid;
  gap: var(--space-xxs);
  grid-template-columns: min-content 1fr;
  margin-bottom: var(--space-xs);
  padding-top: var(--space-xxs);
`;

/**
 * `Terms` styled component.
 */

const Terms = styled(Text).attrs({ variant: 'labelM' })`
  color: var(--color-slate100);
  margin-bottom: var(--space-sm);

  a {
    color: var(--color-cyan400);
  }
`;

/**
 * `InputTimeFrameField` component.
 */

const InputTimeFrameField = ({
  control,
  error,
  register,
  watch
}: {
  control: Control<FormData>;
  error?: string;
  register: UseFormRegister<FormData>;
  watch: UseFormWatch<FormData>;
}) => {
  const { t } = useTranslation('schedule');
  const { formData, updateFormData } = useSchedule();

  const selectedDate = watch('date');
  const isSaturday = new Date(selectedDate).getDay() === 6;
  const isSunday = new Date(selectedDate).getDay() === 0;

  return (
    !isSunday && (
      <FormGroup style={{ marginBottom: '0px' }}>
        <Label htmlFor={'timeFrame'}>{t('form.labels.timeFrame.title')}</Label>

        <div style={{ display: 'flex', gap: 'var(--space-xxs)' }}>
          <InputRadioField
            checked={formData.timeFrame === t('form.labels.timeFrame.slots.1')}
            control={control as Control<any>}
            id={t('form.labels.timeFrame.slots.1')}
            label={t('form.labels.timeFrame.slots.1')}
            onChangeNormalizer={(event, onChange) => {
              updateFormData('timeFrame', t('form.labels.timeFrame.slots.1'));
              onChange(event);
            }}
            value={t('form.labels.timeFrame.slots.1')}
            {...register('timeFrame', { required: t('form.rules.timeFrameRequired') })}
          />

          {!isSaturday && !isSunday && (
            <>
              <InputRadioField
                checked={formData.timeFrame === t('form.labels.timeFrame.slots.2')}
                control={control as Control<any>}
                id={t('form.labels.timeFrame.slots.2')}
                label={t('form.labels.timeFrame.slots.2')}
                onChangeNormalizer={(event, onChange) => {
                  updateFormData('timeFrame', t('form.labels.timeFrame.slots.2'));
                  onChange(event);
                }}
                value={t('form.labels.timeFrame.slots.2')}
                {...register('timeFrame')}
              />
              <InputRadioField
                checked={formData.timeFrame === t('form.labels.timeFrame.slots.3')}
                control={control as Control<any>}
                id={t('form.labels.timeFrame.slots.3')}
                label={t('form.labels.timeFrame.slots.3')}
                onChangeNormalizer={(event, onChange) => {
                  updateFormData('timeFrame', t('form.labels.timeFrame.slots.3'));
                  onChange(event);
                }}
                value={t('form.labels.timeFrame.slots.3')}
                {...register('timeFrame')}
              />
            </>
          )}
        </div>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </FormGroup>
    )
  );
};

/**
 * Export `UserInfo` styled component.
 */

export const UserInfo = () => {
  const isMobile = useBreakpoint(0, breakpoints.md - 1);
  const { t } = useTranslation('schedule');
  const { formData, setStep, updateFormData } = useSchedule();

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch
  } = useForm<FormData>({
    defaultValues: formData,
    mode: 'onChange'
  });

  const normalizePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.replace(/\D/g, '');

    if (value.length > 9) {
      value = value.slice(0, 9);
    }

    value = value.replace(/(\d{0,3})(\d{0,3})(\d{0,3})/, (...args: string[]) => {
      const groups = args.slice(1, -2);

      const result = groups.reduce((acc, group, index) => {
        return group ? acc + (index > 0 ? ' ' : '') + group : acc;
      }, '');

      return result.trim();
    });

    updateFormData('phone', value);
    event.target.value = value;
  };

  const handleOnSubmit = () => {
    setStep(3);
  };

  const animationProps = {
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    initial: { opacity: 0 }
  };

  return (
    <>
      <Wrapper>
        <AnimatePresence mode={'wait'}>
          <Form>
            <AnimatePresence mode={'wait'}>
              <Card {...animationProps}>
                <InputDateField
                  control={control}
                  defaultValue={formData.date}
                  label={t('form.labels.date')}
                  min={new Date().toISOString().split('T')[0]}
                  name={'date'}
                  onChangeNormalizer={(event, onChange) => {
                    updateFormData('date', event.target.value);
                    onChange(event);
                  }}
                  placeholder={t('form.placeholders.date')}
                  rules={{
                    required: t('form.rules.dateRequired'),
                    validate: value => new Date(value).getDay() !== 0 || t('form.rules.noSundays')
                  }}
                />

                <InputTimeFrameField
                  control={control}
                  error={errors.timeFrame?.message}
                  register={register}
                  watch={watch}
                />
              </Card>

              <Card {...animationProps}>
                <InputField
                  control={control}
                  defaultValue={formData.name}
                  label={t('form.labels.name')}
                  name={'name'}
                  onChangeNormalizer={(event, onChange) => {
                    updateFormData('name', event.target.value);
                    onChange(event);
                  }}
                  placeholder={t('form.placeholders.name')}
                  rules={{
                    minLength: {
                      message: t('form.rules.minLength'),
                      value: 3
                    },
                    pattern: {
                      message: t('form.rules.nameComplete'),
                      value: regexes.fullName
                    },
                    required: t('form.rules.nameRequired')
                  }}
                />

                <InputField
                  control={control}
                  defaultValue={formData.phone}
                  label={t('form.labels.phone')}
                  name={'phone'}
                  onChangeNormalizer={(event, onChange) => {
                    normalizePhoneNumber(event);
                    onChange(event);
                  }}
                  placeholder={t('form.placeholders.phone')}
                  rules={{
                    pattern: {
                      message: t('form.rules.phoneMinLength'),
                      value: /^\d{3} \d{3} \d{3}$/
                    },
                    required: t('form.rules.phoneRequired')
                  }}
                />

                <InputField
                  control={control}
                  defaultValue={formData.email}
                  isOptional
                  label={t('form.labels.email')}
                  name={'email'}
                  onChangeNormalizer={(event, onChange) => {
                    updateFormData('email', event.target.value);
                    onChange(event);
                  }}
                  placeholder={t('form.placeholders.email')}
                  rules={{
                    pattern: {
                      message: t('form.rules.email'),
                      value: regexes.email
                    }
                  }}
                  type={'email'}
                />

                <Info>
                  <Svg icon={infoSvg} size={'18px'} style={{ paddingTop: '2px' }} />

                  <Text as={'p'} style={{ color: 'var(--color-slate50)' }} variant={'labelM'}>
                    {t('form.description')}
                  </Text>
                </Info>

                <CheckboxField
                  control={control as Control<any>}
                  label={<RawHtml element={Terms}>{t('form.labels.terms')}</RawHtml>}
                  name={'terms'}
                  rules={{
                    required: t('form.rules.terms')
                  }}
                />

                {isMobile && (
                  <ButtonWrapper>
                    <Button
                      aria-label={t('common:actions.continue')}
                      icon={arrowRightSvg}
                      onClick={handleSubmit(handleOnSubmit)}
                      reverse
                      size={'medium'}
                      type={'submit'}
                    >
                      {t('common:actions.continue')}
                    </Button>
                  </ButtonWrapper>
                )}
              </Card>
            </AnimatePresence>
          </Form>
        </AnimatePresence>
      </Wrapper>

      {!isMobile && (
        <ButtonWrapper>
          <AnimatePresence mode={'wait'}>
            <Button
              aria-label={t('common:actions.continue')}
              icon={arrowRightSvg}
              onClick={handleSubmit(handleOnSubmit)}
              reverse
              size={'medium'}
              type={'submit'}
            >
              {t('common:actions.continue')}
            </Button>
          </AnimatePresence>
        </ButtonWrapper>
      )}
    </>
  );
};
